import {
  ENTITY_CLASS,
  FALL_RISK,
  type FallRisk,
  type FallRiskEntryQuery,
  type UserKey,
} from "@packages/core";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Temporal } from "temporal-polyfill";

import { fetchData, findFallRiskEntries } from "./functions";
import { QueryKey } from "./query";
import { NETWORK_SEMAPHORE } from "./semaphore";
import { TANSTACK_QUERY_CLIENT } from "./tanstack";

export async function findLatestFallRiskEntry(userKey: UserKey | false | null | undefined) {
  if (!userKey) {
    return undefined;
  }
  const { output } = await fetchData({
    type: "Entity::FallRiskEntry",
    input: { condition: [["userKey", "=", userKey]], descendingOrder: true },
  });
  if (output instanceof Error) {
    throw output;
  } else {
    return output;
  }
}

function LatestFallRiskEntryQueryKey(userKey: UserKey | false | null | undefined) {
  return QueryKey(ENTITY_CLASS.FALL_RISK_ENTRY + "::Latest", userKey);
}

export function updateLatestFallRiskEntryQueryCache(
  userKey: UserKey | false | null | undefined,
  newFallRiskEntry?: ReturnType<typeof useLatestFallRiskEntryQuery>["data"] | false,
) {
  if (userKey && newFallRiskEntry !== false && newFallRiskEntry !== undefined) {
    const queryKey = LatestFallRiskEntryQueryKey(userKey);
    TANSTACK_QUERY_CLIENT.setQueryData(
      queryKey,
      function (oldFallRiskEntry: ReturnType<typeof useLatestFallRiskEntryQuery>["data"]) {
        return !oldFallRiskEntry ||
          !newFallRiskEntry ||
          (Temporal.PlainDateTime.compare(
            oldFallRiskEntry.assessedAt,
            newFallRiskEntry.assessedAt,
          ) ||
            Temporal.PlainDateTime.compare(
              oldFallRiskEntry.createdAt,
              newFallRiskEntry.createdAt,
            ) ||
            Temporal.PlainDateTime.compare(
              oldFallRiskEntry.updatedAt,
              newFallRiskEntry.updatedAt,
            )) < 0
          ? newFallRiskEntry
          : oldFallRiskEntry;
      },
    );
  }
}

export function useLatestFallRiskEntryQuery(userKey: UserKey | false | null | undefined) {
  return useQuery({
    queryKey: LatestFallRiskEntryQueryKey(userKey),
    async queryFn() {
      return findLatestFallRiskEntry(userKey);
    },

    enabled: Boolean(userKey),
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}

function FallRiskEntryInfiniteQueryKey(
  ...parameters: Parameters<typeof useFallRiskEntryInfiniteQuery>
) {
  return QueryKey(ENTITY_CLASS.FALL_RISK_ENTRY + "List", ...parameters);
}

export function removeFallRiskEntryInfiniteQueryCache() {
  TANSTACK_QUERY_CLIENT.invalidateQueries({
    queryKey: FallRiskEntryInfiniteQueryKey({}).slice(0, 1),
  });
}

export function useFallRiskEntryInfiniteQuery(
  query: FallRiskEntryQuery,
  options?: Partial<{
    /** 処理を実行する場合に`true`を設定するフラグ */
    enabled: boolean;
  }>,
) {
  return useInfiniteQuery({
    queryKey: FallRiskEntryInfiniteQueryKey(query, options),
    async queryFn(context) {
      const release = await NETWORK_SEMAPHORE.acquire();
      try {
        const { fallRiskEntries, nextPageToken } = await findFallRiskEntries({
          ...query,
          ...context.pageParam,
        });

        for (const fallRiskEntry of fallRiskEntries) {
          updateLatestFallRiskEntryQueryCache(fallRiskEntry.userKey, fallRiskEntry);
        }

        return { fallRiskEntries, nextPageToken };
      } finally {
        release();
      }
    },

    getNextPageParam(lastPage) {
      return lastPage.nextPageToken ? { nextPageToken: lastPage.nextPageToken } : undefined;
    },
    initialPageParam: {} as { nextPageToken?: Record<string, any> },

    enabled: options?.enabled,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}

export function fromFallRiskToColor(fallRisk: FallRisk | false | null | undefined) {
  switch (fallRisk) {
    case FALL_RISK.SEVERE:
      return "#DC2626";
    case FALL_RISK.MODERATE:
      return "#CA8A04";
    case FALL_RISK.MILD:
      return "#0284C7";
    default:
      return "#747476";
  }
}
